import { ReactNode } from 'react'

import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { Flex } from '@cash-design-system/react'

const CIRCLE_RADIUS = 138
const CIRCLE_DIAMETER = CIRCLE_RADIUS * 2
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS

const MIN_EMPTY_PROGRESS_PERCENT = 0.02
const MIN_PROGRESS_PERCENT = 0.006

const ProgressSvg = styled.svg`
  width: 100%;
`

const Circle = styled.circle`
  display: block;
  fill: none;
`

const CircleBackground = styled(Circle)<{ strokeWidth: number; $backgroundColor?: string }>`
  stroke: ${({ $backgroundColor, theme }) => $backgroundColor ?? theme.colors.formFieldBackground};
  stroke-width: ${props => props.strokeWidth};
`

const Container = styled(Flex)`
  position: relative;
`

type CircleProgressProps = {
  strokeWidth: number
  strokeDashoffset: number
  minStrokeDashoffset: number
  $animate: boolean
  $progressColor?: string
}

const CircleProgress = styled(Circle)<CircleProgressProps>`
  /* Rotate the circle so the progress bar starts at the top center, adjusting for
  the round linecap's length that extends past the stroke length. */
  transform: ${({ strokeWidth }) => {
    const radiansToAccountForRoundLinecap = Math.atan2(strokeWidth / 2, CIRCLE_RADIUS)
    const degreesToAccountForRoundLinecap = (radiansToAccountForRoundLinecap * 180) / Math.PI
    return `rotate(${-90 + degreesToAccountForRoundLinecap}deg);`
  }};
  transform-origin: 50% 50%;

  stroke: ${({ $progressColor, theme }) => $progressColor ?? theme.colors.primary};
  stroke-width: ${props => props.strokeWidth};
  stroke-linecap: round;
  stroke-dasharray: ${CIRCLE_CIRCUMFERENCE};
  stroke-dashoffset: ${({ strokeDashoffset }) => strokeDashoffset};
  animation: ${({ $animate, strokeDashoffset, minStrokeDashoffset }) => {
      if (!$animate) {
        return 'none'
      }

      return keyframes({
        // Start from the minimum progress bar length instead of 0 to avoid animation when progress is 0%.
        from: { strokeDashoffset: minStrokeDashoffset },
        to: {
          strokeDashoffset,
        },
      })
    }}
    1000ms forwards;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0ms;
  }
`

type CircleSegmentProps = {
  strokeWidth: number
  strokeDashLength: number
  rotationDegrees: number
}

const CircleSegment = styled(Circle)<CircleSegmentProps>`
  transform: ${({ rotationDegrees }) => `rotate(${rotationDegrees}deg)`};
  transform-origin: 50% 50%;
  stroke-width: ${({ strokeWidth }) => strokeWidth};
  stroke-linecap: round;
  stroke-dasharray: ${({ strokeDashLength }) => `${strokeDashLength} ${CIRCLE_CIRCUMFERENCE - strokeDashLength}`};
`

const CircleBackgroundSegment = styled(CircleSegment)<{ $backgroundColor?: string }>`
  stroke: ${({ $backgroundColor, theme }) => $backgroundColor ?? theme.arcadeColors.semantic.borderSubtle};
`

const CircleProgressSegment = styled(CircleSegment)<{
  $animate: boolean
  $progressColor?: string
  minStrokeDashLength: number
}>`
  stroke: ${({ $progressColor, theme }) => $progressColor ?? theme.arcadeColors.semantic.backgroundBrand};
  animation: ${({ $animate, minStrokeDashLength, strokeDashLength }) => {
      if (!$animate) {
        return 'none'
      }

      return keyframes({
        from: {
          strokeDasharray: `${minStrokeDashLength} ${CIRCLE_CIRCUMFERENCE - minStrokeDashLength}`,
        },
        to: {
          strokeDasharray: `${strokeDashLength} ${CIRCLE_CIRCUMFERENCE - strokeDashLength}`,
        },
      })
    }}
    1000ms forwards;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0ms;
  }
`

const InnerContent = styled(Flex)`
  position: absolute;
`

type CircleArcGeometry = {
  rotationDegrees: number
  strokeDashLength: number
  visibleDegrees: number
}

export type SegmentedCircleGeometry = {
  background: CircleArcGeometry
  gapDegrees: number
  progress: CircleArcGeometry
}

const getRoundLinecapDegrees = (strokeWidth: number) => (Math.atan2(strokeWidth / 2, CIRCLE_RADIUS) * 180) / Math.PI

const getStrokeDashLength = (visibleDegrees: number, strokeWidth: number) => {
  const roundLinecapDegrees = getRoundLinecapDegrees(strokeWidth)
  const centerlineDegrees = Math.max(0, visibleDegrees - roundLinecapDegrees * 2)

  return (centerlineDegrees / 360) * CIRCLE_CIRCUMFERENCE
}

/**
 * Calculates two clockwise arcs whose visible ends are separated by equal gaps.
 * `visibleDegrees` includes each stroke's round caps; `strokeDashLength` does not.
 */
export function getSegmentedCircleGeometry({
  progressPercent,
  progressStrokeWidth,
  backgroundStrokeWidth,
  segmentGapDegrees,
}: {
  progressPercent: number
  progressStrokeWidth: number
  backgroundStrokeWidth: number
  segmentGapDegrees: number
}): SegmentedCircleGeometry {
  const gapDegrees = Number.isFinite(segmentGapDegrees) ? Math.min(Math.max(segmentGapDegrees, 0), 180) : 0
  const normalizedProgress = Number.isFinite(progressPercent) ? Math.min(Math.max(progressPercent, 0), 1) : 0
  const availableDegrees = 360 - gapDegrees * 2
  const progressVisibleDegrees = availableDegrees * normalizedProgress
  const backgroundVisibleDegrees = availableDegrees - progressVisibleDegrees
  const progressLinecapDegrees = getRoundLinecapDegrees(progressStrokeWidth)
  const backgroundLinecapDegrees = getRoundLinecapDegrees(backgroundStrokeWidth)

  return {
    gapDegrees,
    progress: {
      visibleDegrees: progressVisibleDegrees,
      strokeDashLength: getStrokeDashLength(progressVisibleDegrees, progressStrokeWidth),
      rotationDegrees: -90 + gapDegrees + progressLinecapDegrees,
    },
    background: {
      visibleDegrees: backgroundVisibleDegrees,
      strokeDashLength: getStrokeDashLength(backgroundVisibleDegrees, backgroundStrokeWidth),
      rotationDegrees: -90 + progressVisibleDegrees + gapDegrees * 2 + backgroundLinecapDegrees,
    },
  }
}

export function ProgressCircle({
  progressPercent = 0,
  strokeWidthAsPercentageOfRadius = 10 / CIRCLE_RADIUS,
  backgroundStrokeWidthAsPercentageOfRadius,
  animate = true,
  progressColor,
  backgroundColor,
  segmentGapDegrees = 0,
  children,
}: {
  progressPercent: number
  strokeWidthAsPercentageOfRadius?: number
  backgroundStrokeWidthAsPercentageOfRadius?: number
  animate?: boolean
  progressColor?: string
  backgroundColor?: string
  segmentGapDegrees?: number
  children?: ReactNode
}) {
  const requestedProgressPercent = progressPercent
  const progressCircumference = CIRCLE_CIRCUMFERENCE * progressPercent
  const minEmptyProgress = CIRCLE_CIRCUMFERENCE * MIN_EMPTY_PROGRESS_PERCENT

  const progressStrokeWidth = strokeWidthAsPercentageOfRadius * CIRCLE_RADIUS
  const backgroundStrokeWidth =
    (backgroundStrokeWidthAsPercentageOfRadius ?? strokeWidthAsPercentageOfRadius) * CIRCLE_RADIUS
  const maxStrokeWidth = Math.max(progressStrokeWidth, backgroundStrokeWidth)
  // The stroke of a circle svg element is centered around the circumference, so the actual diameter is the diameter of the circle plus half a stroke width on each side.
  const circleTotalDiameter = CIRCLE_DIAMETER + maxStrokeWidth

  const xCoordinateCircleCenter = CIRCLE_RADIUS + maxStrokeWidth / 2
  const yCoordinateCircleCenter = CIRCLE_RADIUS + maxStrokeWidth / 2

  const emptyProgressSpaceWillLookTooSmall = CIRCLE_CIRCUMFERENCE - progressCircumference < minEmptyProgress

  if (progressPercent < 1 && emptyProgressSpaceWillLookTooSmall) {
    progressPercent = (CIRCLE_CIRCUMFERENCE - minEmptyProgress) / CIRCLE_CIRCUMFERENCE
  }

  const progressSpaceWillLookTooSmall =
    progressPercent !== 0 && progressCircumference < MIN_PROGRESS_PERCENT * CIRCLE_CIRCUMFERENCE
  if (progressSpaceWillLookTooSmall) {
    progressPercent = MIN_PROGRESS_PERCENT
  }

  // The round linecap extends past the start and end of the stroke, so this extra length
  // has to be accounted for when calculating the offset.
  const roundLinecapLength = progressStrokeWidth / 2
  const progressStrokeLength = CIRCLE_CIRCUMFERENCE * progressPercent - roundLinecapLength * 2
  const progressStrokeDashoffset = progressPercent >= 1 ? 0 : CIRCLE_CIRCUMFERENCE - progressStrokeLength
  const minProgressStrokeLength = CIRCLE_CIRCUMFERENCE * MIN_PROGRESS_PERCENT - roundLinecapLength * 2
  const minProgressStrokeDashoffset = MIN_PROGRESS_PERCENT >= 1 ? 0 : CIRCLE_CIRCUMFERENCE - minProgressStrokeLength

  const hasSegmentGap = Number.isFinite(segmentGapDegrees) && segmentGapDegrees > 0
  const segmentedProgressPercent = Number.isFinite(requestedProgressPercent)
    ? Math.min(Math.max(requestedProgressPercent, 0), 1)
    : 0
  const segmentedGeometry =
    hasSegmentGap && segmentedProgressPercent > 0 && segmentedProgressPercent < 1
      ? getSegmentedCircleGeometry({
          progressPercent: segmentedProgressPercent,
          progressStrokeWidth,
          backgroundStrokeWidth,
          segmentGapDegrees,
        })
      : undefined
  const minimumSegmentedProgressStrokeLength = segmentedGeometry
    ? getSegmentedCircleGeometry({
        progressPercent: MIN_PROGRESS_PERCENT,
        progressStrokeWidth,
        backgroundStrokeWidth,
        segmentGapDegrees,
      }).progress.strokeDashLength
    : 0

  return (
    <Container justifyContent="center" alignItems="center" fullWidth>
      <ProgressSvg viewBox={`0 0 ${circleTotalDiameter} ${circleTotalDiameter}`}>
        {segmentedGeometry ? (
          <>
            <CircleBackgroundSegment
              strokeWidth={backgroundStrokeWidth}
              strokeDashLength={segmentedGeometry.background.strokeDashLength}
              rotationDegrees={segmentedGeometry.background.rotationDegrees}
              cx={xCoordinateCircleCenter}
              cy={yCoordinateCircleCenter}
              r={CIRCLE_RADIUS}
              $backgroundColor={backgroundColor}
            />
            <CircleProgressSegment
              strokeWidth={progressStrokeWidth}
              strokeDashLength={segmentedGeometry.progress.strokeDashLength}
              rotationDegrees={segmentedGeometry.progress.rotationDegrees}
              minStrokeDashLength={minimumSegmentedProgressStrokeLength}
              cx={xCoordinateCircleCenter}
              cy={yCoordinateCircleCenter}
              r={CIRCLE_RADIUS}
              $animate={animate}
              $progressColor={progressColor}
            />
          </>
        ) : (
          <>
            {(!hasSegmentGap || segmentedProgressPercent < 1) && (
              <CircleBackground
                strokeWidth={backgroundStrokeWidth}
                cx={xCoordinateCircleCenter}
                cy={yCoordinateCircleCenter}
                r={CIRCLE_RADIUS}
                $backgroundColor={backgroundColor}
              />
            )}
            {(!hasSegmentGap ? progressPercent > 0 : segmentedProgressPercent > 0) && (
              <CircleProgress
                strokeWidth={progressStrokeWidth}
                cx={xCoordinateCircleCenter}
                cy={yCoordinateCircleCenter}
                r={CIRCLE_RADIUS}
                radius={CIRCLE_RADIUS}
                strokeDashoffset={progressStrokeDashoffset}
                minStrokeDashoffset={minProgressStrokeDashoffset}
                $animate={animate}
                $progressColor={progressColor}
              />
            )}
          </>
        )}
      </ProgressSvg>
      {children && (
        <InnerContent justifyContent="center" alignItems="center">
          {children}
        </InnerContent>
      )}
    </Container>
  )
}
